import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import {
  Box,
  ButtonBase,
  debounce,
  InputBase,
  MenuItem,
  Paper,
  Popper,
  Typography,
} from '@material-ui/core';
import {
  ArrowDropDown as DownIcon,
  ArrowDropUp as UpIcon,
  Search as SearchIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { AutocompleteCloseReason } from '@material-ui/lab/Autocomplete';
import { FormattedMessage } from 'react-intl';
import { ReactComponent as Incognito } from 'img/spy.svg';

import { ListboxComponent, TextField } from 'common/components';

import messages from './messages';
import commonMessages from 'common/messages/messages';
// styles
import { CustomTheme } from 'common/ui/interfaces';
import SelectedUserOption, {
  userAutocompleteIconAvatarStyles,
  userAutocompleteIconTextStyles,
} from './SelectedUserOption';
import Autocomplete from '../Autocomplete/Autocomplete';
import { INamedEntity } from 'common/interfaces/common';
import { TSearchPosCustomerReturn } from 'common/hooks/useSearchPosCustomers';

const useStyles = makeStyles((theme: CustomTheme) => ({
  search: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 200,
    position: 'relative',
    height: 36,
  },
  wrapIcon: {
    marginLeft: 14,
    height: '100%',
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  wrapSearchIcon: {
    pointerEvents: 'none',
  },
  searchIconIcon: {
    width: '16px',
    height: '16px',
    color: theme.palette.text.secondary,
    opacity: 0.7,
  },
  inputBase: {
    width: '100%',
    borderBottom: '1px solid #dfe2e5',
    '& input': {
      borderRadius: 4,
      backgroundColor: theme.palette.common.white,
      padding: '8px 8px 8px 37px',
      transition: theme.transitions.create(['border-color', 'box-shadow']),
      fontSize: 14,
    },
  },
  itemInput: {
    pointerEvents: 'none',
    '& .MuiOutlinedInput-input': {
      opacity: 0,
    },
  },
  button: {
    width: '100%',
    textAlign: 'left',
    paddingBottom: 0,
  },
  dropIcon: {
    position: 'absolute',
    right: 8,
  },
  popper: {
    border: '1px solid #d6d6d6',
    boxShadow: theme.shadows[5],
    width: '100%',
    zIndex: 10,
    fontSize: 14,
    color: '#d6d6d6',
    backgroundColor: theme.palette.background.paper,
    marginTop: -2,
    marginLeft: 1,
    borderBottomLeftRadius: 3,
    borderBottomRightRadius: 3,
  },
  paper: {
    margin: 0,
    color: '#586069',
    fontSize: 13,
    boxShadow: 'none',
  },
  option: {
    minHeight: 'auto',
    padding: '8px 8px 8px 14px',
    alignItems: 'center',
    '&[data-focus="true"] .guest-status': {
      color: theme.palette.primary.contrastText,
    },
  },
  popperDisablePortal: {
    position: 'relative',
  },
  listBox: {
    borderTop: `1px solid ${theme.palette.secondary.light}`,
  },
  color: {
    width: 14,
    height: 14,
    flexShrink: 0,
    borderRadius: 3,
    marginRight: 8,
    marginTop: 2,
  },
  addOption: {
    padding: `${theme.spacing(1.5, 2)}!important`,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    marginBottom: theme.spacing(1),
    '&.MuiMenuItem-root.Mui-selected': {
      color: theme.palette.background.paper,
      fill: theme.palette.background.paper,
      backgroundColor: theme.palette.primary.main,
    },
    '&.MuiMenuItem-root:hover,&.MuiMenuItem-root:active': {
      color: `${theme.palette.background.paper}!important`,
      fill: theme.palette.background.paper,
      '& >div, >p': {
        color: `${theme.palette.background.paper}!important`,
      },
    },
  },
  notInSystem: {
    marginBottom: 0,
    borderBottom: 'unset',
    marginTop: theme.spacing(1),
  },
  addOptionIcon: {
    marginRight: theme.spacing(1),
    width: '16px',
    height: '16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '1.4rem',
    color: theme.palette.primary.main,
  },
  addOptionText: {
    fontWeight: 500,
  },

  iconAvatar: userAutocompleteIconAvatarStyles(theme),
  itemText: userAutocompleteIconTextStyles(),
}));

type LazyTrigger<T> = (args: T) => any;

export type SearchReferralMembersReturn = LazyTrigger<{ search: string; page: number }>;

interface ISearchUsersAutocompleteProps {
  searchPlaceholder?: string;
  label?: string | JSX.Element;
  selectedValue: any;
  onChange?: (event) => void;
  getOptionsByValue: TSearchPosCustomerReturn;
  renderOption: (any) => React.ReactNode;
  onCreateNew?: () => void;
  disabled?: boolean;
  hideNOS?: boolean;
  isNOSOptionDisabled?: boolean;
  hasStartSearchMode?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
}

const SearchUsersAutocompleteSelect = (props: ISearchUsersAutocompleteProps): JSX.Element => {
  const {
    selectedValue,
    renderOption,
    searchPlaceholder,
    label,
    getOptionsByValue,
    onChange,
    onCreateNew,
    disabled,
    hideNOS,
    isNOSOptionDisabled,
    hasStartSearchMode,
    error,
    helperText,
  } = props;
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const [options, setOptions] = useState<INamedEntity[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event: React.ChangeEvent<any>, reason: AutocompleteCloseReason) => {
    if (reason === 'toggleInput') {
      return;
    }
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'github-label' : undefined;

  const loadReferrals = useCallback(
    async (search: string, reset: boolean = false) => {
      if (search === null && !hasStartSearchMode) {
        setOptions([]);
        setHasMore(false);
        setPage(0);
        return;
      }
      if (reset) {
        setPage(0);
        setHasMore(true);
      }
      const currentPage = reset ? 0 : page;
      await getOptionsByValue({
        search,
        page: currentPage,
      })
        .unwrap()
        .then(response => {
          if (reset) {
            setOptions(response.data || []);
          } else {
            setOptions(prev => [...prev, ...response.data]);
          }
          setHasMore(response.data.length === 30);
          setPage(prev => prev + 1);
        });
    },
    [page, getOptionsByValue, hasStartSearchMode],
  );

  useEffect(() => {
    if (open && searchTerm === null && hasStartSearchMode) {
      setLoadingMore(true);
      setSearchTerm('');
      loadReferrals(searchTerm || '').finally(() => setLoadingMore(false));
    }
  }, [open, searchTerm, hasStartSearchMode, loadReferrals]);

  const onChangeSearch = useMemo(() => {
    return debounce((value: string) => {
      setSearchTerm(value);
      loadReferrals(value, true);
    }, 500);
  }, [loadReferrals]);

  const onScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const list = event.currentTarget;
    if (list.scrollTop + list.clientHeight >= list.scrollHeight - 5 && !loadingMore && hasMore) {
      setLoadingMore(true);
      loadReferrals(searchTerm || '').finally(() => setLoadingMore(false));
    }
  };

  const ListboxProps = {
    itemSize: 45,
    limitCount: 8,
    onScroll,
  };

  const DropIcon = open ? (
    <UpIcon className={classes.dropIcon} />
  ) : (
    <DownIcon className={classes.dropIcon} />
  );

  return (
    <div style={{ position: 'relative' }}>
      <ButtonBase
        disableRipple
        className={classes.button}
        aria-describedby={id}
        onClick={handleClick}
        disabled={disabled}
      >
        <TextField
          variant="outlined"
          label={label}
          value="1"
          style={{ color: 'white' }}
          className={classes.itemInput}
          fullWidth
          error={error}
          helperText={helperText}
        />

        <SelectedUserOption hideNOS={!!hideNOS} selectedValue={selectedValue} />

        {disabled || DropIcon}
      </ButtonBase>
      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement="bottom"
        className={classes.popper}
        disablePortal
      >
        <Autocomplete
          open
          fullWidth
          onClose={handleClose}
          classes={{
            paper: classes.paper,
            option: classes.option,
            popperDisablePortal: classes.popperDisablePortal,
            listbox: classes.listBox,
          }}
          onChange={(event, newValue) => {
            if (onChange) {
              onChange(newValue);
            }

            if (anchorEl) {
              anchorEl.focus();
            }
            setAnchorEl(null);
          }}
          disableCloseOnSelect
          ListboxComponent={ListboxComponent}
          ListboxProps={ListboxProps}
          disablePortal
          options={options}
          renderTags={() => null}
          noOptionsText={<FormattedMessage {...messages.noDataLabel} />}
          renderOption={renderOption}
          getOptionLabel={option => `${option.firstName} ${option.lastName}`}
          loading={loadingMore}
          renderInput={params => (
            <Box className={classes.search}>
              <Box className={`${classes.wrapIcon} ${classes.wrapSearchIcon}`}>
                <SearchIcon className={classes.searchIconIcon} />
              </Box>
              <InputBase
                ref={params.InputProps.ref}
                inputProps={params.inputProps}
                placeholder={searchPlaceholder}
                className={classes.inputBase}
                onChange={e => onChangeSearch(e.target.value)}
                onBlur={() => {
                  setOptions([]);
                  setHasMore(true);
                  setPage(0);
                }}
                onFocus={e => {
                  if (hasStartSearchMode) onChangeSearch(e.target.value);
                }}
                autoFocus
              />
            </Box>
          )}
          PaperComponent={({ children, ...other }) => {
            return (
              <Paper {...other}>
                {!searchTerm && !hideNOS && !hasStartSearchMode ? (
                  <>
                    {!hideNOS && (
                      <MenuItem
                        className={classNames(classes.addOption, classes.notInSystem)}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => {
                          if (onChange) {
                            onChange(undefined);
                          }

                          setAnchorEl(null);
                        }}
                        disabled={isNOSOptionDisabled}
                      >
                        <Incognito className={classes.iconAvatar} />
                        <Typography align="center" className={classes.itemText}>
                          <FormattedMessage {...messages.notInSystem} />
                        </Typography>
                      </MenuItem>
                    )}
                    {onCreateNew && (
                      <MenuItem
                        className={classes.addOption}
                        onMouseDown={e => e.preventDefault()}
                        onClick={onCreateNew}
                      >
                        <Box className={classes.addOptionIcon}>+</Box>
                        <Typography className={classes.addOptionText} color="primary">
                          <FormattedMessage {...commonMessages.newBtn} />
                        </Typography>
                      </MenuItem>
                    )}
                  </>
                ) : (
                  children
                )}
              </Paper>
            );
          }}
          onBlur={() => {
            setOptions([]);
            setHasMore(true);
            setPage(0);
          }}
        />
      </Popper>
    </div>
  );
};

export default SearchUsersAutocompleteSelect;
